/**
 * Discover's search box (#2290): a real text input whose filters are
 * highlighted, with an autocomplete.
 *
 * How the highlighting stays accessible: the `<input>` is the control. Its
 * own text is transparent (the caret is not), and an `aria-hidden` mirror
 * behind it draws the same characters with each token coloured. Both share
 * one set of font metrics (discoverSearch.css), so every glyph sits under its
 * caret position, and the mirror follows the input's horizontal scroll.
 * Screen readers, selection, IME, paste, undo and spellcheck-free typing all
 * belong to the input; the colour layer is decoration only. What a token
 * means is said in words by the chips and the problems line, never only by
 * colour.
 *
 * The autocomplete is the WAI-ARIA combobox pattern: the input is a
 * `combobox` that owns a `listbox` (`aria-controls`, `aria-expanded`,
 * `aria-activedescendant`); ↑/↓ move, Enter or Tab accept, Esc closes.
 * A filter name is never chosen by Enter unless picked with the arrows, so
 * typing a word and pressing Enter never turns it into a filter.
 */
import { Fragment, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type RefObject } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faXmark } from '@fortawesome/pro-solid-svg-icons';
import type { EntityKind, Token } from './searchSyntax';
import { renderHighlight } from './searchHighlight';
import { applySuggestion, suggestContext, type Suggestion } from './searchSuggest';
import { useSearchSuggestions } from './useSearchSuggestions';
import './discoverSearch.css';

export interface SearchBoxProps {
  text: string;
  tokens: Token[];
  /** Where the caret is, as the search state holds it. */
  caret: number | null;
  onType: (text: string, caret: number) => void;
  onReplace: (text: string, caret?: number) => void;
  /** Enter with nothing picked: the token being typed applies. */
  onSettle: () => void;
  onCaret: (caret: number | null) => void;
  onFocusChange: (focused: boolean) => void;
  /** A person, functional or group picked from the list. */
  onLearn: (kind: EntityKind, id: string, label: string) => void;
  onOpenHelp: () => void;
  inputRef?: RefObject<HTMLInputElement | null>;
  /** Ids of what describes the box (its problems line). */
  describedBy?: string;
  invalid?: boolean;
  placeholder?: string;
}

export default function SearchBox({
  text,
  tokens,
  caret,
  onType,
  onReplace,
  onSettle,
  onCaret,
  onFocusChange,
  onLearn,
  onOpenHelp,
  inputRef,
  describedBy,
  invalid = false,
  placeholder = 'Search models, or type tag: author: updated: …',
}: Readonly<SearchBoxProps>) {
  const listId = useId();
  const ownRef = useRef<HTMLInputElement | null>(null);
  const ref = inputRef ?? ownRef;
  const mirrorText = useRef<HTMLSpanElement | null>(null);
  const placeCaret = useRef<number | null>(null);

  const [focused, setFocused] = useState(false);
  const [open, setOpen] = useState(false);

  const ctx = focused && open && caret !== null ? suggestContext(text, caret) : null;
  const { items, loading } = useSearchSuggestions(ctx);
  const ctxKey = ctx ? `${ctx.kind}|${ctx.from}|${ctx.prefix}` : '';

  // A new context starts on its first value; a filter-name list on none, so
  // Enter after a plain word does not pick a filter.
  const firstActive = ctx && ctx.kind !== 'key' ? 0 : -1;
  const [activeFor, setActiveFor] = useState(ctxKey);
  const [active, setActive] = useState(firstActive);
  if (activeFor !== ctxKey) {
    setActiveFor(ctxKey);
    setActive(firstActive);
  }
  const shown = !!ctx && (items.length > 0 || loading);
  const safeActive = active < items.length ? active : -1;

  // Keep the mirror under the input's text as it scrolls, and put the caret
  // where an accepted suggestion leaves it.
  const sync = () => {
    const input = ref.current;
    if (input && mirrorText.current) mirrorText.current.style.transform = `translateX(${-input.scrollLeft}px)`;
  };
  useLayoutEffect(() => {
    const input = ref.current;
    if (input && placeCaret.current !== null) {
      input.setSelectionRange(placeCaret.current, placeCaret.current);
      placeCaret.current = null;
    }
    sync();
  });

  function accept(s: Suggestion) {
    if (!ctx) return;
    if (s.learn) onLearn(s.learn.kind, s.learn.id, s.learn.label);
    const next = applySuggestion(text, ctx, s);
    placeCaret.current = next.caret;
    onType(next.text, next.caret);
    setOpen(true);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!shown) { setOpen(true); return; }
      e.preventDefault();
      const n = items.length;
      if (!n) return;
      if (e.key === 'ArrowDown') setActive((safeActive + 1) % n);
      else setActive(safeActive <= 0 ? n - 1 : safeActive - 1);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      if (shown && safeActive >= 0 && items[safeActive]) {
        e.preventDefault();
        accept(items[safeActive]);
      } else if (e.key === 'Enter') {
        onSettle();
        setOpen(false);
      }
    } else if (e.key === 'Escape') {
      if (shown) {
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
      }
    }
  }

  const caretOf = (el: HTMLInputElement) => el.selectionStart ?? el.value.length;
  const optionId = (i: number) => `${listId}-opt-${i}`;

  return (
    <div className={`dsq-field${invalid ? ' has-problem' : ''}`}>
      <FontAwesomeIcon icon={faMagnifyingGlass} className="dsq-icon" aria-hidden="true"/>
      <input
        ref={ref}
        className="dsq-input"
        type="text"
        role="combobox"
        aria-label="Search models"
        aria-expanded={shown}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={shown && safeActive >= 0 ? optionId(safeActive) : undefined}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        placeholder={placeholder}
        value={text}
        onChange={(e) => {
          onType(e.target.value, caretOf(e.target));
          setOpen(true);
        }}
        onSelect={(e) => { onCaret(caretOf(e.currentTarget)); sync(); }}
        onScroll={sync}
        onKeyDown={onKeyDown}
        onFocus={(e) => {
          setFocused(true);
          setOpen(true);
          onFocusChange(true);
          onCaret(caretOf(e.currentTarget));
        }}
        onBlur={() => {
          setFocused(false);
          setOpen(false);
          onFocusChange(false);
        }}
      />
      <div className="dsq-mirror" aria-hidden="true">
        <span className="dsq-mirror-clip">
          <span ref={mirrorText} className="dsq-mirror-text" data-testid="search-highlight">
            {renderHighlight(text, tokens).map((n, i) => (
              <Fragment
                key={i} // NOSONAR typescript:S6479 false-positive: the mirror's nodes are rebuilt from the input text on every render; stateless and aria-hidden
              >{n}</Fragment>
            ))}
          </span>
        </span>
      </div>
      <div className="dsq-actions">
        {text && (
          <button
            type="button"
            className="dsq-iconbtn"
            aria-label="Clear search"
            title="Clear search"
            onClick={() => { onReplace(''); ref.current?.focus(); }}
          >
            <FontAwesomeIcon icon={faXmark}/>
          </button>
        )}
        <button
          type="button"
          className="dsq-iconbtn is-help"
          aria-label="Search help"
          aria-haspopup="dialog"
          title="Search help: every filter, with examples"
          onClick={onOpenHelp}
        >
          ?
        </button>
      </div>
      <ul id={listId} role="listbox" aria-label="Suggestions" className="dsq-listbox" hidden={!shown}>
        {shown && items.map((s, i) => (
          <li
            key={s.id}
            id={optionId(i)}
            role="option"
            aria-selected={i === safeActive}
            className={`dsq-opt${i === safeActive ? ' is-active' : ''}`}
            onMouseEnter={() => setActive(i)}
            onMouseDown={(e) => { e.preventDefault(); accept(s); }}
          >
            <span className="dsq-opt-label">{s.label}</span>
            {s.detail && (/^\d+$/.test(s.detail)
              ? <span className="dsq-opt-count" aria-label={`${s.detail} models`}>{s.detail}</span>
              : <span className="dsq-opt-detail">{s.detail}</span>)}
          </li>
        ))}
        {shown && loading && (
          <li // NOSONAR typescript:S6819 accessibility-by-design: role=presentation text row in a listbox: role=presentation keeps this "Looking up…" note from being read as a listbox child that is not an option; <img alt> cannot hold its text
            role="presentation"
            className="dsq-opt-note"
          >
            Looking up…
          </li>
        )}
      </ul>
    </div>
  );
}
